<script setup lang="ts">
/**
 * The site language (composables/useAppLocale.ts), as a row of buttons in the
 * account menu and the footer. The choice applies at once and is remembered;
 * pages whose content is English only stay English either way.
 */
import { APP_LOCALES, type AppLocale } from '~/constants/appLocale'

/** Two by two, for a narrow place such as the account menu; one row otherwise. */
defineProps<{ grid?: boolean }>()

const { t } = useI18n()
const { current, choose } = useAppLocale()

/** Each language in its own name, so it can be found by someone who reads only that one. */
const NAMES: Record<AppLocale, string> = { en: 'English', cs: 'Čeština', de: 'Deutsch', pl: 'Polski' }
</script>

<template>
  <div
    role="group"
    :aria-label="t('navbar.language')"
    class="gap-1 bg-surface p-1 dark:bg-gray-900"
    :class="grid ? 'grid grid-cols-2 rounded-2xl' : 'flex items-center rounded-full'"
  >
    <button
      v-for="code in APP_LOCALES"
      :key="code"
      type="button"
      :lang="code"
      :aria-pressed="current === code"
      class="flex-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors duration-300"
      :class="current === code
        ? 'bg-white text-ink shadow-sm dark:bg-gray-700 dark:text-white'
        : 'text-muted hover:text-ink dark:text-gray-400 dark:hover:text-white'"
      @click="choose(code)"
    >
      {{ NAMES[code] }}
    </button>
  </div>
</template>
